/**
 * Maps each color to its basic land name.
 * Used during generation to build the basic land entries.
 */
export const BASIC_LAND_NAMES = {
    W: 'Plains',
    U: 'Island',
    B: 'Swamp',
    R: 'Mountain',
    G: 'Forest',
};
/**
 * Maps each basic land type to the color it produces.
 * Inverse of BASIC_LAND_NAMES — used for fetch land type→color lookups.
 */
export const BASIC_TYPE_TO_COLOR = {
    Plains: 'W',
    Island: 'U',
    Swamp: 'B',
    Mountain: 'R',
    Forest: 'G',
};
/**
 * The complete land cycle catalog.
 * Each cycle lists every card, what it produces, and cycle-level metadata.
 */
export const LAND_CATALOG = [
    // ─── Fetch Lands (On-Color) ────────────────────────────────────────────────
    // On-color: both fetchable types match the color identity.
    // The same 10 cards appear in both on and off — runtime classification.
    {
        category: 'fetch-on',
        label: 'Fetch Lands',
        description: 'Both search types match your colors — always get what you need',
        isTapland: false,
        avgPriceUsd: 12,
        entries: [
            { name: 'Polluted Delta', produces: ['U', 'B'], fetchTypes: ['Island', 'Swamp'] },
            { name: 'Flooded Strand', produces: ['W', 'U'], fetchTypes: ['Plains', 'Island'] },
            { name: 'Bloodstained Mire', produces: ['B', 'R'], fetchTypes: ['Swamp', 'Mountain'] },
            { name: 'Wooded Foothills', produces: ['R', 'G'], fetchTypes: ['Mountain', 'Forest'] },
            { name: 'Windswept Heath', produces: ['W', 'G'], fetchTypes: ['Plains', 'Forest'] },
            { name: 'Verdant Catacombs', produces: ['B', 'G'], fetchTypes: ['Swamp', 'Forest'] },
            { name: 'Misty Rainforest', produces: ['U', 'G'], fetchTypes: ['Island', 'Forest'] },
            { name: 'Scalding Tarn', produces: ['U', 'R'], fetchTypes: ['Island', 'Mountain'] },
            { name: 'Marsh Flats', produces: ['W', 'B'], fetchTypes: ['Plains', 'Swamp'] },
            { name: 'Arid Mesa', produces: ['W', 'R'], fetchTypes: ['Plains', 'Mountain'] },
        ],
    },
    // ─── Fetch Lands (Off-Color) ─────────────────────────────────────────────
    // Off-color: only one fetchable type matches — still grabs typed duals.
    {
        category: 'fetch-off',
        label: 'Off-Color Fetches',
        description: 'One search type matches — fetches shocks, duals, triomes in your colors',
        isTapland: false,
        avgPriceUsd: 12,
        entries: [
            { name: 'Polluted Delta', produces: ['U', 'B'], fetchTypes: ['Island', 'Swamp'] },
            { name: 'Flooded Strand', produces: ['W', 'U'], fetchTypes: ['Plains', 'Island'] },
            { name: 'Bloodstained Mire', produces: ['B', 'R'], fetchTypes: ['Swamp', 'Mountain'] },
            { name: 'Wooded Foothills', produces: ['R', 'G'], fetchTypes: ['Mountain', 'Forest'] },
            { name: 'Windswept Heath', produces: ['W', 'G'], fetchTypes: ['Plains', 'Forest'] },
            { name: 'Verdant Catacombs', produces: ['B', 'G'], fetchTypes: ['Swamp', 'Forest'] },
            { name: 'Misty Rainforest', produces: ['U', 'G'], fetchTypes: ['Island', 'Forest'] },
            { name: 'Scalding Tarn', produces: ['U', 'R'], fetchTypes: ['Island', 'Mountain'] },
            { name: 'Marsh Flats', produces: ['W', 'B'], fetchTypes: ['Plains', 'Swamp'] },
            { name: 'Arid Mesa', produces: ['W', 'R'], fetchTypes: ['Plains', 'Mountain'] },
        ],
    },
    // ─── Shock Lands ──────────────────────────────────────────────────────────────
    {
        category: 'shock',
        label: 'Shock Lands',
        description: 'Pay 2 life or enter tapped — has basic land types',
        isTapland: false,
        isFetchable: true,
        avgPriceUsd: 9,
        entries: [
            { name: 'Steam Vents', produces: ['U', 'R'], basicLandTypes: ['Island', 'Mountain'] },
            { name: 'Watery Grave', produces: ['U', 'B'], basicLandTypes: ['Island', 'Swamp'] },
            { name: 'Blood Crypt', produces: ['B', 'R'], basicLandTypes: ['Swamp', 'Mountain'] },
            { name: 'Stomping Ground', produces: ['R', 'G'], basicLandTypes: ['Mountain', 'Forest'] },
            { name: 'Temple Garden', produces: ['W', 'G'], basicLandTypes: ['Plains', 'Forest'] },
            { name: 'Sacred Foundry', produces: ['W', 'R'], basicLandTypes: ['Plains', 'Mountain'] },
            { name: 'Overgrown Tomb', produces: ['B', 'G'], basicLandTypes: ['Swamp', 'Forest'] },
            { name: 'Hallowed Fountain', produces: ['W', 'U'], basicLandTypes: ['Plains', 'Island'] },
            { name: 'Breeding Pool', produces: ['U', 'G'], basicLandTypes: ['Island', 'Forest'] },
            { name: 'Godless Shrine', produces: ['W', 'B'], basicLandTypes: ['Plains', 'Swamp'] },
        ],
    },
    // ─── Original Dual Lands ──────────────────────────────────────────────────────
    {
        category: 'og-dual',
        label: 'Original Duals (ABU)',
        description: 'No conditions, no drawbacks — the best dual lands ever printed',
        isTapland: false,
        isFetchable: true,
        avgPriceUsd: 500,
        entries: [
            { name: 'Underground Sea', produces: ['U', 'B'], basicLandTypes: ['Island', 'Swamp'] },
            { name: 'Volcanic Island', produces: ['U', 'R'], basicLandTypes: ['Island', 'Mountain'] },
            { name: 'Bayou', produces: ['B', 'G'], basicLandTypes: ['Swamp', 'Forest'] },
            { name: 'Tropical Island', produces: ['U', 'G'], basicLandTypes: ['Island', 'Forest'] },
            { name: 'Tundra', produces: ['W', 'U'], basicLandTypes: ['Plains', 'Island'] },
            { name: 'Badlands', produces: ['B', 'R'], basicLandTypes: ['Swamp', 'Mountain'] },
            { name: 'Taiga', produces: ['R', 'G'], basicLandTypes: ['Mountain', 'Forest'] },
            { name: 'Savannah', produces: ['W', 'G'], basicLandTypes: ['Plains', 'Forest'] },
            { name: 'Plateau', produces: ['W', 'R'], basicLandTypes: ['Plains', 'Mountain'] },
            { name: 'Scrubland', produces: ['W', 'B'], basicLandTypes: ['Plains', 'Swamp'] },
        ],
    },
    // ─── Surveil Lands ────────────────────────────────────────────────────────────
    // Tapland exception — always taps but surveil 1 on entry.
    // All 10 from Murders at Karlov Manor.
    {
        category: 'surveil',
        label: 'Surveil Lands',
        description: 'Enters tapped — surveil 1 on entry',
        isTapland: true,
        isFetchable: true,
        avgPriceUsd: 15,
        entries: [
            { name: 'Meticulous Archive', produces: ['W', 'U'], basicLandTypes: ['Plains', 'Island'] },
            { name: 'Undercity Sewers', produces: ['U', 'B'], basicLandTypes: ['Island', 'Swamp'] },
            { name: 'Raucous Theater', produces: ['B', 'R'], basicLandTypes: ['Swamp', 'Mountain'] },
            { name: 'Commercial District', produces: ['R', 'G'], basicLandTypes: ['Mountain', 'Forest'] },
            { name: 'Lush Portico', produces: ['W', 'G'], basicLandTypes: ['Plains', 'Forest'] },
            { name: 'Shadowy Backstreet', produces: ['W', 'B'], basicLandTypes: ['Plains', 'Swamp'] },
            { name: 'Thundering Falls', produces: ['U', 'R'], basicLandTypes: ['Island', 'Mountain'] },
            { name: 'Underground Mortuary', produces: ['B', 'G'], basicLandTypes: ['Swamp', 'Forest'] },
            { name: 'Elegant Parlor', produces: ['W', 'R'], basicLandTypes: ['Plains', 'Mountain'] },
            { name: 'Hedge Maze', produces: ['U', 'G'], basicLandTypes: ['Island', 'Forest'] },
        ],
    },
    // ─── Check Lands ──────────────────────────────────────────────────────────────
    {
        category: 'check',
        label: 'Check Lands',
        description: 'Enters untapped if you control a matching basic land type',
        isTapland: false,
        avgPriceUsd: 1,
        entries: [
            { name: 'Drowned Catacomb', produces: ['U', 'B'] },
            { name: 'Hinterland Harbor', produces: ['U', 'G'] },
            { name: 'Dragonskull Summit', produces: ['B', 'R'] },
            { name: 'Rootbound Crag', produces: ['R', 'G'] },
            { name: 'Sunpetal Grove', produces: ['W', 'G'] },
            { name: 'Clifftop Retreat', produces: ['W', 'R'] },
            { name: 'Isolated Chapel', produces: ['W', 'B'] },
            { name: 'Woodland Cemetery', produces: ['B', 'G'] },
            { name: 'Sulfur Falls', produces: ['U', 'R'] },
            { name: 'Glacial Fortress', produces: ['W', 'U'] },
        ],
    },
    // ─── Bounce Lands ─────────────────────────────────────────────────────────────
    // Tapland exception — always tap but provide card advantage via bounce.
    {
        category: 'bounce',
        label: 'Bounce Lands',
        description: 'Enters tapped, return a land to hand — effectively two mana per tap',
        isTapland: true,
        avgPriceUsd: 0.4,
        entries: [
            { name: 'Dimir Aqueduct', produces: ['U', 'B'] },
            { name: 'Simic Growth Chamber', produces: ['U', 'G'] },
            { name: 'Rakdos Carnarium', produces: ['B', 'R'] },
            { name: 'Gruul Turf', produces: ['R', 'G'] },
            { name: 'Selesnya Sanctuary', produces: ['W', 'G'] },
            { name: 'Boros Garrison', produces: ['W', 'R'] },
            { name: 'Orzhov Basilica', produces: ['W', 'B'] },
            { name: 'Izzet Boilerworks', produces: ['U', 'R'] },
            { name: 'Golgari Rot Farm', produces: ['B', 'G'] },
            { name: 'Azorius Chancery', produces: ['W', 'U'] },
        ],
    },
    // ─── Triomes ──────────────────────────────────────────────────────────────────
    // Tapland exception. Only eligible for 3+ color identities.
    {
        category: 'triome',
        label: 'Triomes',
        description: 'Enters tapped — three colors, can cycle',
        isTapland: true,
        isFetchable: true,
        avgPriceUsd: 15,
        requiresThreeColors: true,
        entries: [
            {
                name: 'Ketria Triome',
                produces: ['U', 'R', 'G'],
                triomeColors: ['U', 'R', 'G'],
                basicLandTypes: ['Island', 'Mountain', 'Forest'],
            },
            {
                name: 'Indatha Triome',
                produces: ['W', 'B', 'G'],
                triomeColors: ['W', 'B', 'G'],
                basicLandTypes: ['Plains', 'Swamp', 'Forest'],
            },
            {
                name: 'Raugrin Triome',
                produces: ['W', 'U', 'R'],
                triomeColors: ['W', 'U', 'R'],
                basicLandTypes: ['Plains', 'Island', 'Mountain'],
            },
            {
                name: 'Zagoth Triome',
                produces: ['U', 'B', 'G'],
                triomeColors: ['U', 'B', 'G'],
                basicLandTypes: ['Island', 'Swamp', 'Forest'],
            },
            {
                name: 'Savai Triome',
                produces: ['W', 'B', 'R'],
                triomeColors: ['W', 'B', 'R'],
                basicLandTypes: ['Plains', 'Swamp', 'Mountain'],
            },
            {
                name: "Raffine's Tower",
                produces: ['W', 'U', 'B'],
                triomeColors: ['W', 'U', 'B'],
                basicLandTypes: ['Plains', 'Island', 'Swamp'],
            },
            {
                name: "Spara's Headquarters",
                produces: ['W', 'U', 'G'],
                triomeColors: ['W', 'U', 'G'],
                basicLandTypes: ['Plains', 'Island', 'Forest'],
            },
            {
                name: "Ziatora's Proving Ground",
                produces: ['B', 'R', 'G'],
                triomeColors: ['B', 'R', 'G'],
                basicLandTypes: ['Swamp', 'Mountain', 'Forest'],
            },
            {
                name: "Xander's Lounge",
                produces: ['U', 'B', 'R'],
                triomeColors: ['U', 'B', 'R'],
                basicLandTypes: ['Island', 'Swamp', 'Mountain'],
            },
            {
                name: "Jetmir's Garden",
                produces: ['W', 'R', 'G'],
                triomeColors: ['W', 'R', 'G'],
                basicLandTypes: ['Plains', 'Mountain', 'Forest'],
            },
        ],
    },
    // Path of Ancestry is now in the rainbow/staple section
    // ─── Fast Lands ───────────────────────────────────────────────────────────────
    {
        category: 'fast',
        label: 'Fast Lands',
        description: 'Enters untapped if you control 2 or fewer other lands — best early game',
        isTapland: false,
        avgPriceUsd: 3,
        entries: [
            { name: 'Seachrome Coast', produces: ['W', 'U'] },
            { name: 'Darkslick Shores', produces: ['U', 'B'] },
            { name: 'Blackcleave Cliffs', produces: ['B', 'R'] },
            { name: 'Copperline Gorge', produces: ['R', 'G'] },
            { name: 'Razorverge Thicket', produces: ['W', 'G'] },
            { name: 'Concealed Courtyard', produces: ['W', 'B'] },
            { name: 'Spirebluff Canal', produces: ['U', 'R'] },
            { name: 'Blooming Marsh', produces: ['B', 'G'] },
            { name: 'Inspiring Vantage', produces: ['W', 'R'] },
            { name: 'Botanical Sanctum', produces: ['U', 'G'] },
        ],
    },
    // ─── Slow Lands ───────────────────────────────────────────────────────────────
    {
        category: 'slow',
        label: 'Slow Lands',
        description: 'Enters untapped if you control 2 or more other lands — reliable from turn 3 onward',
        isTapland: false,
        avgPriceUsd: 3,
        entries: [
            { name: 'Deserted Beach', produces: ['W', 'U'] },
            { name: 'Shipwreck Marsh', produces: ['U', 'B'] },
            { name: 'Haunted Ridge', produces: ['B', 'R'] },
            { name: 'Rockfall Vale', produces: ['R', 'G'] },
            { name: 'Overgrown Farmland', produces: ['W', 'G'] },
            { name: 'Shattered Sanctum', produces: ['W', 'B'] },
            { name: 'Stormcarved Coast', produces: ['U', 'R'] },
            { name: 'Deathcap Glade', produces: ['B', 'G'] },
            { name: 'Sundown Pass', produces: ['W', 'R'] },
            { name: 'Dreamroot Cascade', produces: ['U', 'G'] },
        ],
    },
    // ─── Reveal Lands ─────────────────────────────────────────────────────────────
    {
        category: 'reveal',
        label: 'Reveal Lands',
        description: 'Enters untapped if you reveal a matching basic land from hand',
        isTapland: false,
        avgPriceUsd: 0.5,
        entries: [
            { name: 'Port Town', produces: ['W', 'U'] },
            { name: 'Choked Estuary', produces: ['U', 'B'] },
            { name: 'Foreboding Ruins', produces: ['B', 'R'] },
            { name: 'Game Trail', produces: ['R', 'G'] },
            { name: 'Fortified Village', produces: ['W', 'G'] },
            { name: 'Shineshadow Snarl', produces: ['W', 'B'] },
            { name: 'Frostboil Snarl', produces: ['U', 'R'] },
            { name: 'Necroblossom Snarl', produces: ['B', 'G'] },
            { name: 'Furycalm Snarl', produces: ['W', 'R'] },
            { name: 'Vineglimmer Snarl', produces: ['U', 'G'] },
        ],
    },
    // ─── Turbulent Lands ──────────────────────────────────────────────────────────
    // Partial cycle — only the 5 enemy-color pairs exist (Secrets of Strixhaven Commander).
    // Allied-color slots have not been printed yet.
    {
        category: 'turbulent',
        label: 'Turbulent Lands',
        description: 'Enters tapped unless your opponents control 8 or more lands',
        isTapland: true,
        isFetchable: true,
        avgPriceUsd: 3,
        entries: [
            { name: 'Turbulent Moor', produces: ['W', 'B'], basicLandTypes: ['Plains', 'Swamp'] },
            { name: 'Turbulent Springs', produces: ['U', 'R'], basicLandTypes: ['Island', 'Mountain'] },
            { name: 'Turbulent Steppe', produces: ['W', 'R'], basicLandTypes: ['Plains', 'Mountain'] },
            { name: 'Turbulent Fen', produces: ['B', 'G'], basicLandTypes: ['Swamp', 'Forest'] },
            { name: 'Turbulent Wilderness', produces: ['U', 'G'], basicLandTypes: ['Island', 'Forest'] },
        ],
    },
    // ─── Verge Lands ──────────────────────────────────────────────────────────────
    {
        category: 'verge',
        label: 'Verge Lands',
        description: 'Always enters untapped — taps for both colors if you control a matching basic',
        isTapland: false,
        avgPriceUsd: 5,
        entries: [
            { name: 'Floodfarm Verge', produces: ['W', 'U'] },
            { name: 'Gloomlake Verge', produces: ['U', 'B'] },
            { name: 'Blazemire Verge', produces: ['B', 'R'] },
            { name: 'Thornspire Verge', produces: ['R', 'G'] },
            { name: 'Hushwood Verge', produces: ['W', 'G'] },
            { name: 'Bleachbone Verge', produces: ['W', 'B'] },
            { name: 'Riverpyre Verge', produces: ['U', 'R'] },
            { name: 'Wastewood Verge', produces: ['B', 'G'] },
            { name: 'Sunbillow Verge', produces: ['W', 'R'] },
            { name: 'Willowrush Verge', produces: ['U', 'G'] },
        ],
    },
    // ─── Marvel Dual Lands ────────────────────────────────────────────────────────
    {
        category: 'marvel-dual',
        label: 'Marvel Dual Lands',
        description: 'Enters untapped — produces colorless, or colored if it entered this turn or you control a basic',
        isTapland: false,
        avgPriceUsd: 4,
        entries: [
            { name: 'Gleaming Bastion', produces: ['W', 'U'] },
            { name: 'Hidden Lair', produces: ['U', 'B'] },
            { name: 'Dark Fortress', produces: ['B', 'R'] },
            { name: 'Training Compound', produces: ['R', 'G'] },
            { name: 'Gathering Place', produces: ['W', 'G'] },
        ],
    },
    // ─── Pain Lands ───────────────────────────────────────────────────────────────
    {
        category: 'pain',
        label: 'Pain Lands',
        description: 'Always enters untapped — tap for either color for 1 damage',
        isTapland: false,
        avgPriceUsd: 1,
        entries: [
            { name: 'Adarkar Wastes', produces: ['W', 'U'] },
            { name: 'Underground River', produces: ['U', 'B'] },
            { name: 'Sulfurous Springs', produces: ['B', 'R'] },
            { name: 'Karplusan Forest', produces: ['R', 'G'] },
            { name: 'Brushland', produces: ['W', 'G'] },
            { name: 'Caves of Koilos', produces: ['W', 'B'] },
            { name: 'Shivan Reef', produces: ['U', 'R'] },
            { name: 'Llanowar Wastes', produces: ['B', 'G'] },
            { name: 'Battlefield Forge', produces: ['W', 'R'] },
            { name: 'Yavimaya Coast', produces: ['U', 'G'] },
        ],
    },
    // ─── Filter Lands ─────────────────────────────────────────────────────────────
    {
        category: 'filter',
        label: 'Filter Lands',
        description: 'Enters untapped — pay colored mana to produce two of either color',
        isTapland: false,
        avgPriceUsd: 8,
        entries: [
            { name: 'Mystic Gate', produces: ['W', 'U'] },
            { name: 'Sunken Ruins', produces: ['U', 'B'] },
            { name: 'Graven Cairns', produces: ['B', 'R'] },
            { name: 'Fire-Lit Thicket', produces: ['R', 'G'] },
            { name: 'Wooded Bastion', produces: ['W', 'G'] },
            { name: 'Fetid Heath', produces: ['W', 'B'] },
            { name: 'Cascade Bluffs', produces: ['U', 'R'] },
            { name: 'Twilight Mire', produces: ['B', 'G'] },
            { name: 'Rugged Prairie', produces: ['W', 'R'] },
            { name: 'Flooded Grove', produces: ['U', 'G'] },
        ],
    },
    // ─── Pathway Lands ────────────────────────────────────────────────────────────
    {
        category: 'pathway',
        label: 'Pathway Lands',
        description: 'Enters untapped — choose one color when played, locked in for the game',
        isTapland: false,
        avgPriceUsd: 4,
        entries: [
            { name: 'Brightclimb Pathway', produces: ['W', 'B'] },
            { name: 'Clearwater Pathway', produces: ['U', 'B'] },
            { name: 'Blightstep Pathway', produces: ['B', 'R'] },
            { name: 'Cragcrown Pathway', produces: ['R', 'G'] },
            { name: 'Branchloft Pathway', produces: ['W', 'G'] },
            { name: 'Hengegate Pathway', produces: ['W', 'U'] },
            { name: 'Riverglide Pathway', produces: ['U', 'R'] },
            { name: 'Darkbore Pathway', produces: ['B', 'G'] },
            { name: 'Needleverge Pathway', produces: ['W', 'R'] },
            { name: 'Barkchannel Pathway', produces: ['U', 'G'] },
        ],
    },
    // ─── Horizon Lands ────────────────────────────────────────────────────────────
    // Only 6 exist (incomplete cycle).
    {
        category: 'horizon',
        label: 'Horizon Lands',
        description: 'Enters untapped — sacrifice to draw a card and prevent flooding',
        isTapland: false,
        avgPriceUsd: 5,
        entries: [
            { name: 'Horizon Canopy', produces: ['W', 'G'] },
            { name: 'Silent Clearing', produces: ['W', 'B'] },
            { name: 'Fiery Islet', produces: ['U', 'R'] },
            { name: 'Nurturing Peatland', produces: ['B', 'G'] },
            { name: 'Sunbaked Canyon', produces: ['W', 'R'] },
            { name: 'Waterlogged Grove', produces: ['U', 'G'] },
        ],
    },
    // ─── Bond Lands ───────────────────────────────────────────────────────────────
    {
        category: 'bond',
        label: 'Bond Lands',
        description: 'Enters untapped in Commander — always untapped with 2+ opponents',
        isTapland: false,
        avgPriceUsd: 7,
        entries: [
            { name: 'Sea of Clouds', produces: ['W', 'U'] },
            { name: 'Morphic Pool', produces: ['U', 'B'] },
            { name: 'Luxury Suite', produces: ['B', 'R'] },
            { name: 'Spire Garden', produces: ['R', 'G'] },
            { name: 'Bountiful Promenade', produces: ['W', 'G'] },
            { name: 'Vault of Champions', produces: ['W', 'B'] },
            { name: 'Training Center', produces: ['U', 'R'] },
            { name: 'Undergrowth Stadium', produces: ['B', 'G'] },
            { name: 'Spectator Seating', produces: ['W', 'R'] },
            { name: 'Rejuvenating Springs', produces: ['U', 'G'] },
        ],
    },
    // ─── Battle Lands ─────────────────────────────────────────────────────────────
    {
        category: 'battle',
        label: 'Battle Lands',
        description: 'Enters untapped with 2+ basics',
        isTapland: false,
        isFetchable: true,
        avgPriceUsd: 1,
        entries: [
            { name: 'Prairie Stream', produces: ['W', 'U'], basicLandTypes: ['Plains', 'Island'] },
            { name: 'Sunken Hollow', produces: ['U', 'B'], basicLandTypes: ['Island', 'Swamp'] },
            { name: 'Smoldering Marsh', produces: ['B', 'R'], basicLandTypes: ['Swamp', 'Mountain'] },
            { name: 'Cinder Glade', produces: ['R', 'G'], basicLandTypes: ['Mountain', 'Forest'] },
            { name: 'Canopy Vista', produces: ['W', 'G'], basicLandTypes: ['Plains', 'Forest'] },
            { name: 'Vernal Fen', produces: ['B', 'G'], basicLandTypes: ['Swamp', 'Forest'] },
        ],
    },
    // ─── Tainted Lands ────────────────────────────────────────────────────────────
    {
        category: 'tainted',
        label: 'Tainted Lands',
        description: 'Always enters untapped — produces Black or paired color if you control a Swamp',
        isTapland: false,
        avgPriceUsd: 1,
        entries: [
            { name: 'Tainted Field', produces: ['W', 'B'] },
            { name: 'Tainted Isle', produces: ['U', 'B'] },
            { name: 'Tainted Peak', produces: ['B', 'R'] },
            { name: 'Tainted Wood', produces: ['B', 'G'] },
        ],
    },
    // ─── Rainbow Lands ───────────────────────────────────────────────────────────
    // Lands that produce any color. Command Tower goes in every Commander deck.
    {
        category: 'rainbow',
        label: 'Rainbow & Special Lands',
        description: 'Produce any color — Commander staples',
        isTapland: false,
        avgPriceUsd: 4,
        entries: [
            // produces: [] means "any color" — always eligible for any 2+ color identity
            { name: 'Command Tower', produces: [] },
            { name: 'Exotic Orchard', produces: [] },
            { name: 'Reflecting Pool', produces: [] },
            { name: 'City of Brass', produces: [] },
            { name: 'Mana Confluence', produces: [] },
            { name: 'Path of Ancestry', produces: [] },
        ],
    },
];
//# sourceMappingURL=land-catalog.js.map